import { ReactNode, useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { cn } from '../../lib/utils'
import type { LucideIcon } from 'lucide-react'
import { ChevronDown } from 'lucide-react'

export interface Tab {
  id: string
  label: string
  icon?: LucideIcon
  suffix?: ReactNode
  /** Optional. Omit for nav-only callers that render content externally (e.g. via react-router <Routes>). */
  content?: ReactNode
}

export interface TabsProps {
  tabs: Tab[]
  activeTab: string
  onTabChange: (tabId: string) => void
  className?: string
  tabsClassName?: string
  contentClassName?: string
}

export function Tabs({
  tabs,
  activeTab,
  onTabChange,
  className,
  tabsClassName,
  contentClassName
}: TabsProps) {
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false)
  const mobileDropdownRef = useRef<HTMLDivElement>(null)
  
  const activeTabObj = useMemo(() => {
    return tabs.find(tab => tab.id === activeTab) || tabs[0]
  }, [tabs, activeTab]);

  // Close mobile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setIsMobileDropdownOpen(false)
      }
    }

    if (isMobileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobileDropdownOpen])

  const handleTabChange = useCallback((tabId: string) => {
    if (tabId !== activeTab) {
      onTabChange(tabId)
    }
  }, [activeTab, onTabChange])

  return (
    <div className={cn('space-y-6', className)}>
      {/* Tab Navigation */}
      <div className={cn('', tabsClassName)}>
        {/* Mobile Dropdown (visible on small screens) */}
        <div className="sm:hidden relative" ref={mobileDropdownRef}>
          <button
            onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
            className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-left transition-colors transition-all duration-200 ease-out hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <div className="flex items-center gap-2">
              {activeTabObj?.icon && <activeTabObj.icon className="h-4 w-4" />}
              <span className="font-medium text-sm">{activeTabObj?.label}</span>
              {activeTabObj?.suffix}
            </div>
            <ChevronDown 
              className={cn(
                "h-4 w-4 transition-transform duration-200 ease-out",
                isMobileDropdownOpen ? "rotate-180" : ""
              )} 
            />
          </button>
          
          {isMobileDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg z-50 overflow-hidden">
              <div className="p-0">
                {tabs.map((tabItem, index) => (
                  <button
                    key={tabItem.id}
                    onClick={() => {
                      handleTabChange(tabItem.id)
                      setIsMobileDropdownOpen(false)
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 p-3 text-left transition-colors transition-all duration-150 ease-out',
                      'hover:bg-gray-50 dark:hover:bg-gray-800 outline-none',
                      index === 0 && 'rounded-t-md',
                      index === tabs.length - 1 && 'rounded-b-md',
                      activeTab === tabItem.id
                        ? 'bg-primary/10 text-primary dark:bg-primary/15 border-l-2 border-primary'
                        : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {tabItem.icon && <tabItem.icon className="h-4 w-4" />}
                    <span className="font-medium text-sm">{tabItem.label}</span>
                    {tabItem.suffix}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Horizontal Tabs (hidden on small screens) */}
        <div className="hidden sm:block">
          <div className="flex flex-wrap gap-x-6 gap-y-4 px-1 border-b border-border">
            {tabs.map((tabItem) => (
              <button
                key={tabItem.id}
                onClick={() => handleTabChange(tabItem.id)}
                className={cn(
                  'pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap outline-none transition-colors',
                  activeTab === tabItem.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                )}
              >
                {tabItem.icon && <tabItem.icon className="h-4 w-4" />}
                {tabItem.label}
                {tabItem.suffix}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content Container - only render active tab to avoid eager side effects.
          Skipped entirely when the active tab has no content (nav-only callers). */}
      {activeTabObj && activeTabObj.content != null && (
        <div className={cn('mt-6', contentClassName)}>
          <div key={activeTabObj.id}>
            {activeTabObj.content}
          </div>
        </div>
      )}
    </div>
  )
}